import { useState } from 'react';
import { Loader2, AlertCircle } from 'lucide-react';
import { getApiBaseUrl } from '@/config/api.config';

interface ScormPreviewProps {
  item: any; // content item with scorm_id + scorm_metadata
}

/**
 * Admin-side SCORM preview for the Edit Course page.
 * Loads the SCO through the same content proxy as the learner player (which injects a
 * SCORM API shim), so the SCO initializes cleanly. No tracking here — any CMI the shim
 * posts is simply ignored (no listener), this is preview only.
 */
export default function ScormPreview({ item }: ScormPreviewProps) {
  const [loading, setLoading] = useState(true);

  const meta = item?.scorm_metadata || {};
  const scorm_id: string | undefined = item?.scorm_id;
  const s3_html_url: string = item?.media_assets?.s3_html_url || '';

  // Derive package id + path from scorm_metadata, falling back to the S3 URL
  // (which always contains .../scorm/{package_id}/{path}).
  const s3Match = s3_html_url.match(/\/scorm\/([^/]+)\/([^?]+)/);
  const package_id = (scorm_id ? scorm_id.split(':')[0] : undefined) || (s3Match ? s3Match[1] : undefined);
  const launch_href = meta?.launch_href || (s3Match ? s3Match[2] : undefined);

  // Proxy URL (same as learner player) so the SCO gets a window.API and renders cleanly.
  const launch_url = (() => {
    if (!package_id || !launch_href) return s3_html_url;
    const base = getApiBaseUrl().replace(/\/$/, '');
    const [path, hrefQuery] = launch_href.split('?');
    const params = [hrefQuery, (meta?.parameters || '').replace(/^\?/, '')].filter(Boolean).join('&');
    return `${base}/scorm/content/${package_id}/${path}${params ? `?${params}` : ''}`;
  })();

  if (!launch_url) {
    return (
      <div className="text-center py-8">
        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <AlertCircle className="w-8 h-8 text-gray-400" />
        </div>
        <p className="text-gray-500 font-medium">SCORM content not available</p>
        <p className="text-sm text-gray-400 mt-1">No launch file found for this lesson.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="relative h-[600px] min-h-[600px]">
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/70 z-10">
            <Loader2 className="w-8 h-8 animate-spin text-brand-blue-500" />
          </div>
        )}
        <iframe
          title={item?.title || 'SCORM Content'}
          src={launch_url}
          className="w-full h-full border-0"
          onLoad={() => setLoading(false)}
          sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-modals"
        />
      </div>
    </div>
  );
}
